import TaxStatusEnum from '@constants/enums/taxStatus';
import OfferStatusEnum from '@constants/enums/offer/status';

import i18n from '@plugins/i18n';

import Collection from '@/models/Collection';
import Offer from '@/models/Offer';

const PanelOfferTypeEnum = Object.freeze({
    FUND: 'FUND',
    SINGLE_COMPANY: 'SINGLE_COMPANY'
});

export class PanelOfferCollection extends Collection {
    static type = 'PanelOfferCollection';
    static endpoint = Offer.api.index;
    static model = Offer;

    static fields() {
        return {
            ...super.fields(),
            offer_ids: this.attr(null),
            closed: this.boolean(false),
            off_panel: this.boolean(false)
        };
    }

    get preset_filters() {
        const filters = {};

        if (Array.isArray(this.offer_ids) && this.offer_ids.length > 0) {
            filters.id = `${this.filterOperator('in')}:[${this.offer_ids.join(',')}]`;
        } else if (Array.isArray(this.offer_ids) && this.offer_ids.length === 0) {
            // Force empty response when panel has no offers.
            filters.id = `${this.filterOperator('is')}:__none__`;
        }
        if (this.closed) {
            filters.closed = 'true';
        }
        if (this.off_panel) {
            filters.off_panel = 'true';
        }

        return filters;
    }

    get headers() {
        return [
            this.createHeader('name', i18n.t('name'), {
                filterable: true,
                sortable: true,
                sticky: true,
                width: '18rem',
                minWidth: '18rem'
            }),
            this.createHeader('offer_type', i18n.t('offer_type'), {
                format: 'enum',
                filterable: {
                    operators: this.getOperators(['eq', 'in'])
                },
                enum: PanelOfferTypeEnum,
                enumA11yKey: '_page.settings.restricted_panels.offer_type',
                width: '10rem',
                transform: data => {
                    if (data.item.single_company === true) {
                        return PanelOfferTypeEnum.SINGLE_COMPANY;
                    }

                    if (data.item.fund || data.item.tax_status === 'VCT') {
                        return PanelOfferTypeEnum.FUND;
                    }

                    return null;
                }
            }),
            this.createHeader('tax_status', i18n.t('tax_status'), {
                format: 'enum',
                filterable: true,
                enum: TaxStatusEnum,
                enumA11yKey: 'enums.tax_status_short',
                formatOptions: {
                    tooltip: v => i18n.t(`enums.tax_status.${v}`)
                },
                width: '8rem'
            }),
            this.createHeader('status', i18n.t('status'), {
                format: 'enum',
                width: '7rem',
                filterable: true,
                sortable: true,
                enum: OfferStatusEnum,
                enumA11yKey: 'enums.offer_status',
                enumTypes: {
                    [OfferStatusEnum.OPEN]: 'info',
                    [OfferStatusEnum.CLOSED]: 'error',
                    [OfferStatusEnum.EVERGREEN]: 'success',
                    [OfferStatusEnum.COMING_SOON]: 'warning'
                }
            }),
            this.createHeader('close_at', i18n.t('close_at'), {
                format: 'date',
                filterable: true,
                width: '9rem'
            })
        ];
    }

    transformFilters(filters = {}) {
        const transformed = { ...filters };
        const offerTypeFilter = transformed.offer_type;

        if (typeof offerTypeFilter === 'string' && offerTypeFilter.length > 0) {
            const [operator, rawCriteria] = offerTypeFilter.includes(':')
                ? offerTypeFilter.match(/([^:]+):(.*)/).slice(1)
                : [this.filterOperator('is'), offerTypeFilter];

            let selectedTypes = [];

            if (operator === this.filterOperator('in')) {
                selectedTypes = rawCriteria
                    .replace(/[[\]\s]/g, '')
                    .split(',')
                    .filter(Boolean);
            } else {
                selectedTypes = [rawCriteria];
            }

            selectedTypes = selectedTypes.map(value => `${value}`.toUpperCase());

            delete transformed.offer_type;

            if (selectedTypes.length === 1) {
                if (selectedTypes[0] === PanelOfferTypeEnum.SINGLE_COMPANY) {
                    transformed.single_company = 'true';
                } else if (selectedTypes[0] === PanelOfferTypeEnum.FUND) {
                    transformed.single_company = 'false';
                }
            }
        }

        return transformed;
    }

    get default_headers() {
        return ['name', 'offer_type', 'tax_status', 'status', 'close_at'];
    }
}

export default PanelOfferCollection;
